// @ts-nocheck
import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import { isEmpty } from 'lodash-es'
import { useRouter } from 'next/router'

import { CONSTANTS, ORDER_TYPES, Page } from 'utils/constants'
import Infra from 'mobx/Infra'
import Home from 'mobx/Home'
import User from 'mobx/User'
import Application from 'mobx/Application'

import ImageComponent from 'themes/ImageComponent'
import Skeleton from 'components/Skeleton'
import Banners, { type Banner } from 'components/deals/Banners'
import { respondAbove } from '../../styles/mixins'
import LimitedTimeOffersSection from '../components/deals/LimitedTimeOffersSection'
import DealsAndOffersSection from '../components/deals/DealsAndOffersSection'
import filterItemsAvailability from '../../utils/filterItemsAvailability'
import { getTranslatedTextByKey, sendRequest, getECommerceDomainByEnv, isMobile } from 'utils/utils'
import CartSummaryBase from 'components/cart/CartSummary'
import { handleBannerByTarget, validateDealIfLocalized } from 'utils/dealsUtils'
import { sendCustomEvent } from 'utils/analytics/analytics'
import { useStores } from 'hooks/useStores'
import { StoreContext } from 'contexts/StoreContext'

const CartSummary = styled(CartSummaryBase)`
	position: fixed;
	padding: 0;
`

const Container = styled.div`
	display: flex;
	flex-flow: column nowrap;
	align-items: center;
	width: 100%;
	min-width: 310px;
	max-width: 1400px;
	margin: 0 auto;
	background-color: var(--clear);
`

const DealsAndOffersPadding = styled.div`
	display: flex;
	flex-flow: column nowrap;
	align-items: stretch;
	gap: 60px;
	width: 100%;
	padding: 40px 20px;

	${respondAbove.sm`
		gap: 40px;
		padding: 40px 20px;
	`}
	${respondAbove.md`
		gap: 45px;
		padding: 45px 20px;
	`}
    ${respondAbove.lg`
		gap: 60px;
		padding: 60px 20px;
	`}
`

const Deals = ({ deals, params }) => {
	const [dealsData, setDealsData] = useState({
		banners: deals?.banners ?? [],
		offers: deals?.offers ?? [],
		ltos: {
			delivery: filterItemsAvailability(deals?.ltos?.delivery ?? []),
			pickup: filterItemsAvailability(deals?.ltos?.pickup ?? []),
		},
	})

	const [isDelivery, setIsDelivery] = useState()
	const isStoreClosed = Home.areAllStoresClosed()
	const [showCart, setShowCart] = useState(false)
	const { couponFlowStore, cartStore, addressManagerV2Store } = useStores()
	const router = useRouter()
	const { setStore } = useContext(StoreContext)

	useEffect(() => {
		setShowCart(true)
	}, [])

	const loadMenuAndInitSession = async () => {
		const { menuRes, storeMetaData, serverSession, menuUrl } = await addressManagerV2Store.onMenuClickWhenLocalized(cartStore, User)
		await User.setSession(serverSession)

		setStore((prevStore: any) => ({ ...prevStore, data: menuRes, metaData: storeMetaData }))

		return menuUrl
	}

	const isImageURLValid = (url) => {
		try {
			// Will throw an error if url is not valid
			// eslint-disable-next-line no-new
			new URL(url)
		} catch (e) {
			return false
		}
		return /\/[\w-]+\.(png|jpeg|jpg|webp|avif|gif)(|\?[^/]*)$/i.test(url)
	}

	/**
	 * if the user is localized but clicked on a banner with different order type
	 * (and he is previously localized to it and we have his related address in local storage),
	 * we need to load the menu for the new order type
	 * @param {*} orderTypeDelivery
	 * @returns
	 */
	const loadOrderType = async (orderTypeDelivery) => {
		// get the address from local storage
		const addressLocalStorage = addressManagerV2Store.getAddressFromLocaleStorage()

		// if the address is not empty, load the menu for the store associated with that address
		if (!isEmpty(addressLocalStorage)) {
			// get the store id associated with the address
			const storeId = orderTypeDelivery ? addressLocalStorage?.delivery?.storeId : addressLocalStorage?.pickup?.storeId

			if (storeId) {
				// load the menu and init the session for the store associated with the address
				const newMenuPath = await loadMenuAndInitSession()

				// return the new menu path
				return newMenuPath
			}
		}

		// return null if the address is empty
		return null
	}

	const setDealsDataAndFilter = (response, isDelivery) => {
		if (addressManagerV2Store.isUserLocalized()) {
			setDealsData({
				banners: response?.banners.filter((banner) => validateDealIfLocalized(banner, isDelivery)) ?? [],
				offers: (response?.offers.filter((offer) => validateDealIfLocalized(offer, isDelivery)) ?? []).filter((item) =>
					isImageURLValid(item?.imgDesktopUrl)
				),
				ltos: {
					delivery: isDelivery ? filterItemsAvailability(response?.ltos?.delivery ?? []).filter((item) => !!item?.description?.en_ID) : [],
					pickup: !isDelivery ? filterItemsAvailability(response?.ltos?.pickup ?? []).filter((item) => !!item?.description?.en_ID) : [],
				},
			})
		} else {
			setDealsData({
				banners: response?.banners ?? [],
				offers: (response?.offers ?? []).filter((item) => isImageURLValid(item?.imgDesktopUrl) && !!item?.offerTitle?.length),
				ltos: {
					delivery: filterItemsAvailability(response?.ltos?.delivery ?? []).filter((item) => !!item?.description?.en_ID),
					pickup: filterItemsAvailability(response?.ltos?.pickup ?? []).filter((item) => !!item?.description?.en_ID),
				},
			})
		}
	}

	const openStoreClosedPopup = () => {
		const title = getTranslatedTextByKey('webviewFlow.storesAreClosed', 'sorry all our stores are currently closed')
		const message = getTranslatedTextByKey('webviewFlow.orderMessage', 'Please order during our working hours')
		Infra.setNotification({
			open: true,
			title,
			message,
			topImg: <ImageComponent asset="/images/pastOrdersEmpty.png" />,
			customAction: () => {
				Infra.closeNotification()
			},
			customButtonType: 'secondary',
			cancelText: getTranslatedTextByKey('menu.backToMenu', 'Back to menu'),
			showCancelButton: false,
			closeAction: true,
			cancelAction: () => {
				Infra.closeNotification()
			},
		})

		sendCustomEvent({
			category: 'error',
			action: 'notification',
			label: title,
			message,
		})
	}

	const onBannerAndOfferClick = async (banner: Banner) => {
		if (isStoreClosed) {
			openStoreClosedPopup()
			return
		}

		handleBannerByTarget({ banner, couponFlowStore, setStore, cartStore, router, addressManagerV2Store })
	}

	const onLimitedTimeOfferClick = async (item, deliveryType) => {
		if (isStoreClosed) {
			openStoreClosedPopup()
			return
		}
		const { description } = item
		if (description?.en_ID) {
			const couponCode = description.en_ID
			couponFlowStore.start({ code: couponCode, orderTypeToForce: deliveryType, openModal: false })
		} else {
			console.error(`item id: ${item.id} is missing its description.en_ID, so it cannot work as a limited time offer!`)
		}
	}

	useEffect(() => {
		const isDelivery = addressManagerV2Store.getLocalizedOrderType() === ORDER_TYPES.DELIVERY
		;(async () => {
			try {
				if (typeof Application.isMobileApp !== 'undefined') {
					const channelType = Application.isMobileApp ? CONSTANTS.CHANNEL_TYPE.APP : CONSTANTS.CHANNEL_TYPE.WEB
					// on Home page channelType can be only Web or App
					// get deals, assign them to state
					const response = await sendRequest(
						false,
						`${getECommerceDomainByEnv()}/v1/tenants/${params.c}/deals2?lang=${params.l}&channelType=${channelType}`,
						'get',
						{
							'content-type': 'application/x-www-form-urlencoded;charset=utf-8',
						},
						undefined,
						undefined,
						undefined,
						undefined,
						false
					)
					if (response) {
						setDealsDataAndFilter(response, isDelivery)
					}
				}
			} catch (error) {
				console.error(error)
				// redirect to homepage
			} finally {
				setIsDelivery(isDelivery)
			}
		})()
	}, [Application.isMobileApp])

	const onStartOrderButtonClick = async () => {

		// On new localization flow, we don't do anything since this method is not called in Skeleton (SubHeaderGroup not rendered)
		if(addressManagerV2Store.newLocalizationFlowEnabled) {
			return
		}

		if (isStoreClosed) {
			openStoreClosedPopup()
			return
		}
		const menuPath = await loadOrderType(isDelivery)

		if (menuPath) {
			window.location.replace(`${window.location.origin}${menuPath}`)
		}
	}

	const staticPage = (
		<Skeleton page={Page.DEALS} showStoreInfo showLogo showCustomTitle showHeader onStartOrderButtonClick={onStartOrderButtonClick}>
			<Container>
				{/* Banners carousel */}
				<Banners banners={dealsData.banners} onBannerAndOfferClick={onBannerAndOfferClick} />

				<DealsAndOffersPadding data-testId="deals-and-offers-padding">
					{/* Deals and offers */}
					{!!dealsData.offers.length && <DealsAndOffersSection list={dealsData.offers} onBannerAndOfferClick={onBannerAndOfferClick} />}

					{/* Limited time offers (ltos) */}
					{!!dealsData.ltos.delivery.length && (
						<LimitedTimeOffersSection
							items={dealsData.ltos.delivery}
							offerType="delivery"
							onLimitedTimeOfferClick={onLimitedTimeOfferClick}
						/>
					)}
					{!!dealsData.ltos.pickup.length && (
						<LimitedTimeOffersSection
							items={dealsData.ltos.pickup}
							offerType="pickup"
							onLimitedTimeOfferClick={onLimitedTimeOfferClick}
						/>
					)}
				</DealsAndOffersPadding>
			</Container>
			{showCart && addressManagerV2Store.isUserLocalized() && isMobile(false) && (
				<CartSummary isMenu isOpen={showCart} isHover={!Application.isMobileApp} />
			)}
		</Skeleton>
	)

	return staticPage
}

export default Deals
